// ==============================
// Imports
// ==============================

// MUI Components
import { SelectChangeEvent } from '@mui/material';

// Libraries
import React, { useCallback, useEffect, useState } from 'react';

// Types
import {
  TrialBooking,
  Subtype,
  Response,
  PaginatedResponse,
  ServiceType,
} from '../types/TrialBookingTable.type';

// Local Components
import { apiGet } from '../utils/apiClient';
import dayjs, { Dayjs } from 'dayjs';
import { saveAs } from 'file-saver';
import { notifyAPIClient } from '../utils/toast';

export const useTrialBookingTable = () => {
  // Data
  const [trialBookingList, setTrialBookingList] = useState<TrialBooking[]>([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [subTypeFilter, setSubTypeFilter] = useState<Subtype>({
    id: -1,
    name: 'All',
  });
  const [resultFilter, setResultFilter] = useState<string>('ALL');
  const [subtypeOptions, setSubtypeOptions] = useState<Subtype[]>([]);
  const [filterDate, setFilterDate] = useState<Dayjs | null>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  // Dialog States
  const [selectedTrialBooking, setSelectedTrialBooking] =
    useState<TrialBooking | null>(null);
  const [openBookingInfoDialog, setOpenBookingInfoDialog] = useState(false);

  // Last sync time for trial booking data
  const [lastSyncTime, setLastSyncTime] = useState<dayjs.Dayjs | null>(null);

  // Highlighted row
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);

  // Networking
  const fetchTrialBookingList = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await apiGet<PaginatedResponse<TrialBooking>>(
        `/trial-booking/list?page=${page}&limit=${limit}&search=${searchTerm}&subtypeId=${subTypeFilter?.id}&result=${resultFilter}&date=${
          filterDate && filterDate.isValid()
            ? filterDate.format('YYYY-MM-DD')
            : ''
        }`,
      );

      setTrialBookingList(res.data);
      setTotalItems(res.metadata.totalItems);
      setLastSyncTime(dayjs());
    } catch (error) {
      console.error('Failed to fetch trial booking list:', error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm, subTypeFilter?.id, resultFilter, filterDate]);

  const fetchSubtypeOptions = async () => {
    try {
      const res = await apiGet<Response<ServiceType>>(
        `/service/filter?name=COURSE`,
      );
      setSubtypeOptions(res.data[0]?.subtypes ?? []);
    } catch (error) {
      console.error('Failed to fetch subtype options:', error);
      setSubtypeOptions([]);
    }
  };

  // Hooks
  useEffect(() => {
    fetchTrialBookingList();
  }, [page, limit, subTypeFilter, resultFilter, filterDate]);

  // ==============================
  // Update 09/06/2025: Fetch trial booking data every 3 seconds
  // ==============================
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTrialBookingList(true);
    }, 3000);

    return () => clearInterval(interval);
  }, [fetchTrialBookingList]);

  useEffect(() => {
    fetchSubtypeOptions();
  }, []);

  // Handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterSubtypeChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    const selectedSubtype = subtypeOptions.find(
      (option) => option.id === Number(newValue),
    ) || {
      id: -1,
      name: 'All',
    };

    setSubTypeFilter(selectedSubtype);
    setPage(1);
  };

  // ==============================
  // Update 09/06/2025: Clear all filters
  // ==============================
  const clearFilters = () => {
    setSearchTerm('');
    setSubTypeFilter({ id: -1, name: 'All' });
    setResultFilter('ALL');
    setFilterDate(null);

    setPage(1);
    setLimit(10);
  };

  const emptyRows = limit - trialBookingList.length;

  const handleCloseTrialBookingDialog = () => {
    setSelectedTrialBooking(null);
    setOpenBookingInfoDialog(false);
  };

  const calculateAge = (dob: string | Date) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatResultString = (str: string) => {
    return str.replace(/_/g, ' ');
  };

  const handleDownloadTrialBookingList = async () => {
    setDownloadLoading(true);
    try {
      const queryParams = new URLSearchParams();

      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }

      queryParams.append('subtypeId', `${subTypeFilter?.id ?? -1}`);
      queryParams.append('result', resultFilter || 'ALL');

      if (filterDate?.isValid()) {
        queryParams.append('date', filterDate.format('YYYY-MM-DD'));
      }

      const response = await apiGet<Blob>(
        `/trial-booking/download?${queryParams.toString()}`,
        {
          responseType: 'blob',
        },
      );

      const blob = new Blob([response as unknown as BlobPart], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const dateLabel = filterDate?.isValid()
        ? filterDate.format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD');

      saveAs(blob, `trial_booking_list_${dateLabel}.xlsx`);
    } catch (error) {
      console.error('Download trial booking data failed:', error);
      notifyAPIClient(
        'Failed to download trial booking list. Please try again later.',
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  return {
    trialBookingList,
    page,
    limit,
    totalItems,
    searchTerm,
    subTypeFilter,
    resultFilter,
    setResultFilter,
    filterDate,
    setFilterDate,
    emptyRows,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    handleFilterSubtypeChange,
    clearFilters,
    fetchTrialBookingList,
    setPage,
    subtypeOptions,
    openBookingInfoDialog,
    setOpenBookingInfoDialog,
    selectedTrialBooking,
    setSelectedTrialBooking,
    handleCloseTrialBookingDialog,
    calculateAge,
    formatResultString,
    handleDownloadTrialBookingList,
    downloadLoading,
    loading,
    lastSyncTime,
    highlightedRow,
    setHighlightedRow,
  };
};
